import { useMemo } from "react";
import { Helmet } from "react-helmet-async";

import {
	DEFAULT_DOCUMENT_DESCRIPTION,
	DEFAULT_DOCUMENT_TITLE,
	DOCUMENT_TITLE_SUFFIX,
} from "./page-meta-defaults";
import { isDev, isPrd } from "../chillnn/enviroment";

const DefaultImageUrl = "/public/meta/default.jpeg";

export type Props = {
	title: string;
	url: string;
	description?: string;
	imageUrl?: string;

	children: React.ReactNode;
};

export const PageMeta = ({
	title: _title,
	url: _url,
	description: _description,
	imageUrl: _imageUrl,
	children,
}: Props) => {
	const publicUrl = window.location.host;

	const title = useMemo(() => {
		let pageTitle = "";

		if (isDev) pageTitle += "[Dev] ";

		if (_title) {
			pageTitle += ` ${_title} | ${DOCUMENT_TITLE_SUFFIX}`;
		} else {
			pageTitle += DEFAULT_DOCUMENT_TITLE;
		}

		return pageTitle;
	}, [_title]);

	const imageUrl = useMemo(() => _imageUrl ?? `${publicUrl}${DefaultImageUrl}`, [_imageUrl, publicUrl]);

	const url = useMemo(() => {
		return `${publicUrl}${_url}`;
	}, [_url, publicUrl]);

	const description = useMemo(
		() => _description ?? DEFAULT_DOCUMENT_DESCRIPTION,
		[_description],
	);

	/**
	 * インデックスの登録と表示に関するメタタグ
	 * @see https://developers.google.com/search/docs/crawling-indexing/robots-meta-tag?hl=ja#directives
	 */
	const robotsContent = useMemo<"noindex" | "all">(() => {
		if (isPrd) return "all";

		return "noindex";
	}, []);

	return (
		<>
			<Helmet>
				<title>{title}</title>
				<meta name="description" content={description} />
				<meta name="image" content={imageUrl} />

				<meta property="og:url" content={url} />
				<meta property="og:type" content="website" />
				<meta property="og:title" content={title} />
				<meta property="og:description" content={description} />
				<meta property="og:image" content={imageUrl} />

				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={title} />
				<meta name="twitter:description" content={description} />
				<meta name="twitter:image" content={imageUrl} />

				<meta name="robots" content={robotsContent} />
			</Helmet>

			{children}
		</>
	);
};
